// Finnish message bundle. Keys are nested namespaces (i18next-compatible shape)
// so a future migration is a t() swap, not a re-key of every string.
//
// NEVER put HTML/JSX markup in a message — use a {slot} placeholder and tRich.
// scripts/audit/i18n-keys.cjs fails the build on markup, missing keys and orphans.
export const fi = {
    common: {
        loading: "Ladataan…",
        // Deliberately bilingual: names the ACTIVE language in its own language and
        // the action in the target language, so the tooltip reads either way.
        languageToggle: "Suomi — switch to English",
        skipToContent: "Siirry sisältöön",
        dialog: {
            close: "Sulje",
        },
        footer: {
            terms: "Käyttöehdot",
            calculator: "Betonilaskuri",
            pumpingPrice: "Betonipumppauksen hinta",
            coverageAreas: "Toimitusalueet",
            game: "Betonipeli",
            privacy: "Tietosuoja",
            cookieSettings: "Evästeasetukset",
            contact: "Yhteydenotto",
        },
        nav: {
            joinCta: "Liity Jerryyn",
            myRequests: "Tarjouspyynnöt",
            requestQuoteCta: "Pyydä tarjous",
            defaultUserName: "Käyttäjä",
            myDetails: "Omat tiedot",
            logout: "Kirjaudu ulos",
            impersonateAction: "Kirjaudu käyttäjänä…",
            switchCompany: "Vaihda asiointiroolia",
            myOrders: "Omat valut",
            support: "Tuki",
            admin: "Hallinta",
            // Public main-nav link in App.jsx's Toolbar — a static HTML page
            // generated into dist/, distinct from the footer's coverageAreas.
            coverageAreas: "Toimitusalueet",
        },
        auth: {
            switchCompanyFailed: "Yrityksen vaihto epäonnistui",
            impersonateFailed: "Impersonointi epäonnistui",
            extendSessionFailed: "Session jatkaminen epäonnistui",
            appleNotConfigured: "Apple-kirjautumista ei ole konfiguroitu",
            appleLoadFailed: "Apple-kirjautumisen lataus epäonnistui",
            linkedinNotConfigured: "LinkedIn-kirjautumista ei ole konfiguroitu",
        },
        subscribePush: {
            promptLabel: "Tilaa selaimen ilmoitukset",
            successLabel: "Ilmoitukset päällä",
            iosNeedsPwaHint: "Lisää Betonijerry aloitusnäytölle saadaksesi ilmoitukset puhelimeen.",
            deniedHint: "Selaimen ilmoitukset on estetty. Voit sallia ne selaimen asetuksista.",
            subscribing: "Tilataan…",
        },
        fcm: {
            signInRequired: "Kirjaudu sisään tilataksesi ilmoitukset.",
            subscribeFailed: "Ilmoitusten tilaus epäonnistui.",
            registerDeviceFailed: "Laitteen rekisteröinti epäonnistui.",
        },
        addressAutocomplete: {
            label: "Katuosoite",
            noSuggestions: "Ei ehdotuksia",
            typeFreely: "Voit kirjoittaa osoitteen vapaasti",
        },
        pageMeta: {
            defaultTitle: "Betonipumppaus Suomessa | Pyydä tarjous | BetoniJerry",
            defaultDescription:
                "Pyydä ilmainen tarjous betonipumppauksesta lähes koko Suomessa ilman puhelinrumbaa. Yksi pyyntö tavoittaa alueesi pumppaajat, ja saat tarjoukset nopeasti — vertaile hintoja ja valitse itse. Ilman sitoumuksia.",
        },
        coverageSentence: {
            fallback:
                "Pumppaajia pääkaupunkiseudulla, Tampereella, Jyväskylässä, Lahdessa ja muualla Etelä-, Kaakkois- ja Itä-Suomessa.",
        },
    },
    // Finnish values here are fallback-only — resolveErrorMessage (src/i18n/errorCopy.js)
    // always prefers the live err.message on lang==="fi", so these only render if a
    // response is somehow missing its message body. Kept byte-identical to the current
    // backend literals (no added punctuation) so a fallback render can't drift from
    // what the server actually says.
    errors: {
        tooManyAttempts: "Liian monta yritystä. Yritä hetken kuluttua uudelleen.",
        addressCheckRateLimited: "Liian monta osoitepyyntöä. Yritä hetken kuluttua uudelleen.",
        requestAlreadySent: "Tarjouspyyntö on jo lähetetty",
        requestNotEditable: "Tarjouspyyntö ei ole enää muokattavissa",
        requestHasOffers: "Tarjouspyyntöä ei voi perua: siihen on jo tullut tarjouksia",
        requestNotFound: "Tarjouspyyntöä ei löydy",
        noAccountAccess: "Sinulla ei ole oikeutta tilata tällä tilillä",
        accountNotFound: "Tällä sähköpostiosoitteella ei ole tunnusta.",
        loginSessionInvalid: "Koodi on vanhentunut. Pyydä uusi koodi.",
        loginCodeInvalid: "Väärä koodi. Tarkista koodi sähköpostista ja yritä uudelleen.",
        generic: "Jokin meni pieleen. Yritä uudelleen.",
        networkError: "verkkovirhe",
    },
    // Per-operation fallback copy for catch blocks whose thrown error carries no
    // stable `code` (see src/i18n/errorCopy.js) — resolveErrorMessage's `lang === "fi"`
    // branch still prefers the live err.message first; these are the ultimate fallback
    // so the English UI never falls through to raw Finnish server prose.
    auth: {
        checkFailed: "Tarkistus epäonnistui",
        loginFailed: "Kirjautuminen epäonnistui",
        codeSendFailed: "Koodin lähetys epäonnistui",
        codeLoginFailed: "Kirjautuminen koodilla epäonnistui",
        resetCodeSendFailed: "Palautuskoodin lähetys epäonnistui",
        passwordChangeFailed: "Salasanan vaihto epäonnistui",
        passwordChangedLoginFailed: "Salasana vaihdettu onnistuneesti, mutta sisäänkirjautuminen epäonnistui ({reason}). Avaa \"Salasana\"-välilehti ja kirjaudu uudella salasanalla.",
        passwordSetLoginFailed: "Salasana asetettu onnistuneesti, mutta sisäänkirjautuminen epäonnistui ({reason}). Päivitä sivu ja kirjaudu uudella salasanalla.",
        registerCodeSendFailed: "Vahvistuskoodin lähetys epäonnistui",
        verifyCodeInvalid: "Vahvistuskoodi virheellinen",
        registerFailed: "Tilin luominen epäonnistui",
        googleLoginFailed: "Google-kirjautuminen epäonnistui",
        microsoftLoginFailed: "Microsoft-kirjautuminen epäonnistui",
        appleLoginFailed: "Apple-kirjautuminen epäonnistui",
        linkedinLoginFailed: "LinkedIn-kirjautuminen epäonnistui",

        // Strings reused verbatim across multiple auth files — one key each so
        // the same label/word can't drift out of sync between call sites
        // (mirrors the wizard.shared convention in the wizard namespace below).
        shared: {
            cancel: "Peruuta",
            save: "Tallenna",
            saving: "Tallennetaan…",
            saveFailed: "Tallennus epäonnistui",
            loginButton: "Kirjaudu",
            loggingIn: "Kirjaudutaan…",
            emailLabel: "Sähköposti",
            etunimiLabel: "Etunimi",
            etunimiRequired: "Etunimi pakollinen",
            sukunimiLabel: "Sukunimi",
            sukunimiRequired: "Sukunimi pakollinen",
            puhelinLabel: "Puhelin",
            puhelinRequired: "Puhelinnumero pakollinen",
            passwordLabel: "Salasana",
            min8Chars: "Vähintään 8 merkkiä",
            verificationCodeLabel: "Vahvistuskoodi",
            companyNameLabel: "Yrityksen nimi",
            companyNameRequired: "Yrityksen nimi pakollinen",
            ytunnusLabel: "Y-tunnus",
            streetAddressLabel: "Katuosoite",
            postalCodeLabel: "Postinumero",
            cityLabel: "Kaupunki",
            removeFavorite: "Poista pikavalinnoista",
            addFavorite: "Tallenna pikavalintaan",
        },

        login: {
            title: "Kirjaudu sisään",
            emailPrefix: "Sähköposti:",
            changeEmail: "Vaihda",
            emailCodeMethod: "Sähköpostikoodi",
            forgotPasswordMethod: "Unohtuiko salasana?",
            setPasswordMethod: "Aseta salasana",
            codeRequired: "Anna kirjautumiskoodi",
            resetCodeRequired: "Anna palautuskoodi",
            passwordMinLength: "Salasanan tulee olla vähintään 8 merkkiä",
            sendCodeIntro: "Lähetämme 4-numeroisen kirjautumiskoodin osoitteeseen {email}.",
            sending: "Lähetetään…",
            sendCodeButton: "Lähetä koodi",
            codeSentTo: "Koodi lähetetty osoitteeseen {email}. Anna 4-numeroinen koodi alle.",
            codeFieldLabel: "Kirjautumiskoodi",
            loginWithCodeButton: "Kirjaudu koodilla",
            resendCode: "Lähetä uusi koodi",
            resetIntroWithPassword: "Lähetämme palautuskoodin osoitteeseen {email}. Sen jälkeen voit asettaa uuden salasanan.",
            resetIntroSetPassword: "Lähetämme vahvistuskoodin osoitteeseen {email}. Sen jälkeen voit asettaa salasanan tilillesi.",
            sendResetCodeButton: "Lähetä palautuskoodi",
            sendVerifyCodeButton: "Lähetä vahvistuskoodi",
            resetVerifyIntroWithPassword: "Anna palautuskoodi ja uusi salasana.",
            resetVerifyIntroSetPassword: "Anna vahvistuskoodi ja valitse salasana.",
            resetCodeLabel: "Palautuskoodi",
            newPasswordLabel: "Uusi salasana",
            changingPassword: "Vaihdetaan…",
            settingPassword: "Asetetaan…",
            changePasswordAndLogin: "Vaihda salasana ja kirjaudu",
            setPasswordAndLogin: "Aseta salasana ja kirjaudu",
            resendResetCode: "Lähetä uusi palautuskoodi",
            resendVerifyCode: "Lähetä uusi vahvistuskoodi",
        },

        register: {
            stepCode: "Koodi",
            stepProfile: "Profiili",
            stepIndicator: "Vaihe {current} / {total} · {label}",
            verifyCodeRequired: "Anna vahvistuskoodi",
            ytunnusRequired: "Y-tunnus pakollinen",
            legalRequired: "Sinun tulee hyväksyä käyttöehdot",
            checkEmailTitle: "Tarkista sähköposti",
            sendingCodeIntro: "Lähetämme vahvistuskoodia osoitteeseen {email}…",
            codeSentIntro: "Lähetimme 4-numeroisen vahvistuskoodin osoitteeseen {email}.",
            verifying: "Vahvistetaan…",
            verifyCodeButton: "Vahvista koodi",
            useDifferentEmail: "Käytä eri sähköpostia",
            profileTitle: "Täydennä tilisi",
            isCompanyLabel: "Olen yritys",
            legalConsent: "Hyväksyn {terms}. Lue myös {privacy}.",
            termsLinkText: "käyttöehdot",
            privacyLinkText: "tietosuojaseloste",
            creatingAccount: "Luodaan tiliä…",
            createAccountButton: "Luo tili",
        },

        social: {
            microsoftInProgress: "Aiempi Microsoft-kirjautuminen on vielä kesken. Yritä hetken kuluttua uudelleen.",
            appleNoToken: "Applelta ei saatu ID-tokenia",
            orContinueWith: "tai jatka tilillä",
            continueWithMicrosoft: "Jatka Microsoftilla",
            continueWithApple: "Jatka Applella",
            continueWithLinkedin: "Jatka LinkedInillä",
            legalConsent: "Jatkamalla hyväksyt {terms}. Lue myös {privacy}.",
            termsLinkText: "käyttöehdot",
            privacyLinkText: "tietosuojaseloste",
        },

        company: {
            editTitle: "Muokkaa yritystä",
            offersWillGoTo: "Tarjouspyynnöt lähetetään: {email}",
            noEmailWarning: "Henkilöllä ei ole sähköpostiosoitetta — tarjouspyynnöt ohjautuvat laskutussähköpostiin tai ylläpitäjille",
            nameTooLong: "Nimi on liian pitkä (max 250 merkkiä)",
            ytunnusFormat: "Anna y-tunnus muodossa 1234567-8",
            ytunnusConflict: "Y-tunnus on jo käytössä toisella yrityksellä",
            billingAddressLabel: "Laskutusosoite",
            introSectionTitle: "Esittely",
            introHelperText: "Tämä teksti näkyy asiakkaalle tarjouksen yhteydessä.",
            settingsLoadFailed: "Esittelyn ja Jerry-asetusten lataus epäonnistui. Perustiedot voi silti tallentaa.",
            introPlaceholder: "Kerro lyhyesti yrityksestäsi: kalusto, kokemus, palvelualue…",
            publicListingTitle: "Julkinen yritysesittely",
            publicListingHelper: "Voit julkaista yrityksesi betonijerry.fi:n julkisilla sivuilla, jolloin asiakkaat löytävät sinut myös hakukoneista.",
            websiteLabel: "Kotisivu",
            // LEGAL ARTIFACT — the record of what a provider agreed to publish.
            // Must stay character-for-character identical: it enumerates exactly
            // six published fields and one explicit exclusion. Do not reword,
            // shorten or "improve" this value. See merge-betonijerry-report.md.
            publicListingConsent: "Julkaise yritykseni betonijerry.fi:n julkisilla sivuilla. Julkaistavat tiedot: nimi, Y-tunnus, esittely, kotisivun osoite, logo ja toiminta-alue. Yhteyshenkilön nimeä, puhelinnumeroa tai sähköpostiosoitetta ei julkaista.",
            uploadLogo: "Lataa logo",
            logoUploaded: "Logo ladattu ✓",
            logoUploadFailed: "Logon lataus epäonnistui",
            publicUrlCaption: "Julkinen osoite: {link} (julkaistaan seuraavan päivityksen yhteydessä)",
            jerrySettingsTitle: "Jerry-asetukset",
            jerrySettingsHelper: "Valitse henkilö Jerry-tarjouksille, uudet tarjouspyynnöt lähetetään siihen sähköpostiosoitteeseen.",
            contactPersonLabel: "Yhteyshenkilö Jerry-tarjouksille",
            noneSelected: "Ei valittu",
            personFallback: "Henkilö {id}",
            noEmailAddress: "ei sähköpostiosoitetta",
            offerEmailLabel: "Tarjouspyyntöjen sähköpostiosoite (valinnainen)",
            offerEmailHelper: "Voi olla yhteinen postilaatikko, esim. tarjoukset@yritys.fi. Kirjautuminen palveluun tapahtuu silti aina henkilökohtaisella sähköpostiosoitteella.",
            offerEmailInvalid: "Tarkista sähköpostiosoite (esim. tarjoukset@yritys.fi)",
            offersGoToOverride: "Tarjouspyynnöt lähetetään alla olevaan osoitteeseen — tämä henkilö kirjautuu ja vastaa niihin",
        },

        person: {
            emailRequired: "Sähköposti pakollinen",
            emailInvalid: "Sähköpostiosoite on virheellinen",
            emailConflict: "Sähköpostiosoite on jo käytössä toisella tilillä",
            editTitle: "Muokkaa yhteystietoja",
            billingAddressLabel: "Laskutusosoite (yksityishenkilönä)",
            useSiteAddressLabel: "Sama kuin työmaan osoite",
        },

        impersonate: {
            forbidden: "Ei oikeutta tämän käyttäjän impersonointiin.",
            notFound: "Kohdehenkilöä ei löytynyt. Tarkista sähköpostiosoite.",
            genericFailed: "Impersonointi epäonnistui.",
            dialogTitle: "Kirjaudu käyttäjänä",
            savedLabel: "Tallennetut",
            emailLabel: "Käyttäjän sähköposti",
        },

        impersonateBanner: {
            unknownUser: "käyttäjä",
            statusLine: "Impersonointi: {name} · {time}",
            extendButton: "Jatka 10 min",
            stopButton: "Lopeta",
        },

        account: {
            menuAriaLabel: "Käyttäjävalikko",
            companyPrefix: "Yritys: {company}",
        },

        linkedin: {
            backToHome: "Palaa etusivulle",
            finishing: "Viimeistellään LinkedIn-kirjautumista…",
        },

        email: {
            title: "Kirjaudu tai luo tili",
            intro: "Anna sähköpostiosoitteesi — tunnistamme, onko sinulla jo tili.",
            invalidEmail: "Anna voimassa oleva sähköposti",
            checking: "Tarkistetaan…",
            continueButton: "Jatka",
        },
    },
    offers: {
        saveFailed: "Tarjouksen tallennus epäonnistui",
        sendFailed: "Lähetys epäonnistui",
        acceptFailed: "Tarjouksen hyväksyminen epäonnistui.",
    },
    wizard: {
        privateBillingCreateFailed: "Yksityishenkilön laskutustiedon luominen epäonnistui",
        sendFailed: "Lähetys epäonnistui",

        // Strings reused verbatim across multiple wizard files — one key each
        // so the same label/word can't drift out of sync between call sites.
        shared: {
            rasitusluokkaLabel: "Rasitusluokka",
            suunnitteluikaLabel: "Suunnitteluikä",
            years50: "50 vuotta",
            years100: "100 vuotta",
            kayttokohdeLabel: "Käyttökohde",
            cancel: "Peruuta",
            startOver: "Aloita alusta",
            example: "Esim.",
        },

        // "N pumppufirma(a)" — reused by AddressFeasibilityPanel + StepPump.
        units: {
            pumppufirmaOne: "pumppufirma",
            pumppufirmaOther: "pumppufirmaa",
        },

        validation: {
            addressRequired: "Anna työmaan osoite",
            addressNotFound: "Tarkista osoitteen kirjoitusasu — emme löytäneet sitä kartalta",
            amountRequired: "Anna betonin määrä (m³)",
            amountMin: "Vähimmäismäärä on 1 m³",
            amountStep: "Anna määrä 0,5 m³ portain (esim. 1, 1,5, 2)",
            kayttokohdeRequired: "Valitse käyttökohde",
            raekokoConflict: "Linjapumppaus — käytä hieno 16 mm tai 8 mm",
            pumpBothZero: "Anna joko puomin tai linjan pituus — molemmat eivät voi olla 0",
            termsRequired: "Hyväksy ehdot lähettääksesi tarjouspyynnön",
            laskutusRequired: "Valitse laskutettava yritys tai henkilö.",
        },

        stepAddress: {
            title: "Työmaan osoite",
            jerryMessage: "Aloitetaan työmaan osoitteesta — tarkistan samalla, onko pumppufirmaa alueellasi. Maksuton ja ei-sitova; lähettäminen vaatii lopuksi ilmaisen tilin.",
            intro: "Kirjoita työmaan katuosoite ja valitse oikea osoite ehdotuksista.",
            addressLabel: "Osoite",
            addressHelper: "Esim. Mannerheimintie 12, 00100 Helsinki",
        },

        addressFeasibility: {
            checking: "Tarkistetaan osoitetta…",
            errorTitle: "Tarkistus epäonnistui",
            errorFallback: "Tuntematon virhe — yritä hetken kuluttua uudelleen.",
            retry: "Yritä uudelleen",
            notFoundTitle: "Emme löytäneet tätä osoitetta",
            notFoundBody: "Tarkista kirjoitusasu — kokeile esim. lisätä postinumero.",
            noSupplyTitle: "Ei vielä tällä alueella",
            noSupplyBody: "Voit silti jättää tarjouspyynnön — ilmoitamme sähköpostilla, kun palvelu laajenee alueellesi.",
            nearestVarikko: "{km} km lähimmälle varikolle",
            successTitle: "Pumppufirmoja tällä alueella",
            successBody: "{count} {unit} alueella.",
        },

        stepPump: {
            title: "Pumppu",
            heading: "Kuinka kaukana pumppu on valukohteesta?",
            intro: "Pumppuauto pystyttää {boom} lähimpään mahdolliseen kohtaan, ja tarvittaessa puomin kärjestä jatketaan vielä {linja} eli pidemmällä putkella tai letkulla. Anna molemmat metrimäärät, niin pumppufirma tietää, mitä kalustoa työmaalle tarvitaan.",
            boomWord: "puomin",
            linjaWord: "linjalla",
            boomSectionTitle: "Puomin pituus",
            boomFieldLabel: "Puomin vähimmäispituus",
            boomAriaLabel: "Puomin pituus metreinä",
            boomHelper: "Pienin puomikokoluokka, jolla pumppuauton pystytyspaikalta yltää kauimmaiseen valukohtaan.",
            noBoomLabel: "Ei puomia — pumpataan suoraan auton perästä",
            boomResultTitle: "Pumppufirmoja tällä puomilla",
            boomResultBody: "{count} {unit} pystyy toimittamaan tällä puomilla.",
            boomNoneTitle: "Ei näin pitkää puomia alueella",
            boomNoneBody: "Alueen pumppufirmoilla ei ole näin pitkää puomia — voit silti lähettää tarjouspyynnön.",
            linjaLabel: "Linjan pituus",
            linjaAriaLabel: "Linjan pituus metreinä",
            linjaHelper: "Erillisen lisäletkun tai putkilinjan pituus pumpun perästä tai puomin kärjestä kauimmaiseen valukohtaan.",
            noLinjaLabel: "Ei lisälinjaa — betoni tippuu suoraan puomin päästä",
            roikostaSuffix: "(roikosta)",
            bothEmptyNote: "Etkö tiedä etäisyyksiä? Voit jättää kentät tyhjäksi — pumppufirma arvioi tarpeen tarjousta tehdessään.",
            totalLabel: "Yhteensä noin",
        },

        stepConcrete: {
            title: "Betoni",
            calculatorLoadFailed: "Laskurin lataus epäonnistui. Päivitä sivu ja yritä uudelleen.",
            amountSectionTitle: "Määrä ja työaika",
            amountPlaceholder: "esim. 10",
            amountAriaLabel: "Betonin määrä kuutiometreinä",
            amountHelper: "Tilattava määrä — väh. 1 m³, 0,5 m³ portain",
            kestoPlaceholder: "esim. 2",
            kestoAriaLabel: "Arvioitu pumppausaika tunteina",
            kestoHelper: "Arvioitu työaika",
            editFloorPlan: "Muokkaa pohjakuvaa (laskuri)",
            drawFloorPlan: "Piirrä pohjakuva ja laske määrä",
            leftoverBoth: "Puomista ja linjasta",
            leftoverBoom: "Puomista",
            leftoverLinja: "Linjasta",
            leftoverBody: "{lead} jää pumppaamatonta betonia noin {amount}. Tilaa 0,5 m³ portain. Ylijäävä betoni jää työmaalle tai toimitetaan kierrätykseen maksua vastaan.",
            leftoverAmount: "{litres} l ({m3} m³)",
            lujuusLabel: "Lujuusluokka",
            raekokoLabel: "Raekoko",
            notkeusLabel: "Notkeus",
            helpMeChoose: "Auta minua valitsemaan",
            or: "— tai —",
            iKnowExact: "Tiedän tarkan betonilaadun",
            guidedResultTitle: "Avustimen tulos",
            reopenGuide: "Avaa avustin uudelleen",
            switchToManual: "Vaihda käsin syöttöön",
            descriptionLabel: "Kuvaus valukohteesta ja erikoisvaatimukset",
            descriptionHelperMuu: "Tarkenna 'Muu' -valinnat tähän (esim. lujuusluokka, raekoko, notkeus, rasitusluokka)",
            descriptionHelper: "Mitä valetaan? Kerro myös betonin erikoisvaatimukset (esim. pakkasenkesto, kuidut, valkosementti) ja muut tarpeet.",
            useGuideAnyway: "Käytä avustinta sittenkin",
            calculatorTitle: "Betonilaskuri",
            useAmount: "Käytä määrä",
        },

        stepReview: {
            title: "Tarkista tarjouspyyntö",
            sectionAddress: "Osoite",
            addressLabel: "Työmaan osoite",
            noSupplyWarning: "Alueella ei vielä pumppufirmaa — pyyntö rekisteröidään odottamaan kattavuutta.",
            boomFromRear: "Perästä pumppaus",
            providerEstimates: "Pumppufirma arvioi",
            linjaFromTip: "Roikosta (suoraan puomin päästä)",
            guidedBadge: "Avustimella selvitetty",
            amountLabel: "Määrä",
            kestoLabel: "Arvioitu pumppausaika",
            lujuusLabel: "Lujuus",
            descriptionLabel: "Kuvaus",
            attachmentsLabel: "Liitteet",
            attachmentsCount: "{n} kpl",
            noDetails: "Ei lisätietoja",
            sectionContact: "Yhteystiedot",
            nameLabel: "Nimi",
            phoneLabel: "Puhelin",
            emailLabel: "Sähköposti",
            notAuthenticated: "Et ole kirjautunut sisään. Pyyntöä ei voi lähettää ilman yhteystietoja — kirjaudu tai luo tili alla.",
            readMore: "Lue lisää",
        },

        stepDetails: {
            title: "Lisätiedot",
            driveLabel: "Ajo-ohjeet",
            driveHelper: "Esim. portin koodi, kapea kulkutie, työmaan portin sijainti. Älä kirjoita tähän yhteystietoja — ne kysytään erikseen.",
            otherLabel: "Muut tiedot",
            otherHelper: "Mitä tahansa muuta pumppufirman pitäisi tietää. Älä kirjoita tähän yhteystietoja — ne kysytään erikseen.",
            attachmentsAuthNote: "Voit lisätä liitteitä (kuvat, PDF), kun olet kirjautunut sisään — myös pyynnön lähettämisen jälkeen.",
        },

        quoteStepper: {
            stepAriaLabel: "Vaihe {current} / {total}",
        },

        navButtons: {
            next: "Seuraava →",
            back: "← Edellinen",
            send: "Lähetä tarjouspyyntö",
            sending: "Lähetetään…",
            needAccount: "Tarvitset tilin lähettääksesi — ilmainen.",
            alreadyHaveAccount: "Onko sinulla jo tili? Kirjaudu",
        },

        resumeNotice: {
            title: "Jatkat keskeneräistä tarjouspyyntöä",
            startedAt: "aloitettu {when}",
            confirmPrompt: "Poistetaanko se?",
            hideAriaLabel: "Piilota ilmoitus",
        },

        quoteWizard: {
            checkAddressNext: "Tarkista osoite →",
            autosaveAuth: "Tarjouspyyntösi tallentuu automaattisesti — voit jatkaa myöhemmin samasta kohdasta.",
            autosaveAnon: "Antamasi tiedot tallentuvat selaimeesi ja niitä käytetään anonyymisti palvelun parantamiseen — poistuvat 30 päivässä, ellet kirjaudu sisään.",
        },

        kiitos: {
            title: "Kiitos!",
            openBody: "Tarjouspyyntö {id} vastaanotettu — välitämme sen pumppufirmoille alueellasi. Vastauksia tulee sähköpostiin {email}.",
            noSupplyBody: "Tarjouspyyntö {id} vastaanotettu. Tällä alueella ei vielä ole pumppufirmaa, mutta saat ilmoituksen sähköpostiin {email}, heti kun palvelu laajenee alueellesi.",
            jerryMessage: "Saat ilmoituksen sähköpostiin heti, kun ensimmäinen tarjous saapuu.",
            followOffers: "Seuraa tarjouksia",
        },

        jerryPill: {
            imgAlt: "Jerry — pumppupalvelusi opas",
            greeting: "Hei, Jerry tässä.",
            defaultMessage: "Autan sinua eteenpäin.",
        },

        kayttoIkaToggle: {
            defaultBadge: "OLETUS",
        },

        sectionHeader: {
            defaultEditLabel: "Muokkaa",
        },

        specGuideModal: {
            title: "Avustin betonin valintaan",
            intro: "Oikea betoni riippuu siitä, millaiset olosuhteet rakennetta rasittavat — onko se sisällä vai ulkona, kuivassa vai kosteassa, pakkasessa vai suojassa. Vastaa muutamaan kysymykseen rakenteesta, niin avustin päättelee sopivan rasitusluokan ja sen mukaisen lujuusluokan tilausta varten.",
            presetCaption: "Käyttökohteesta esitäytetty:",
            selectKayttokohdeFirst: "Valitse ensin käyttökohde ennen avustimen avaamista.",
            applyThese: "Käytä nämä",
        },

        specGuideResult: {
            recommendedTitle: "Suositeltu betoni",
            minLujuusLabel: "Minimi lujuusluokka",
            coverManual: "Rakennesuunnitelman mukaan",
            coverLabel: "Betonipeite",
        },

        rasitusluokkaField: {
            helperDefault: "Valitse käyttökohdettasi vastaava luokka, tai käytä yllä olevaa avustinta",
        },

        laskutusSection: {
            title: "Laskutus",
            switchLabel: "Vaihda",
            billedToLabel: "Laskutettava",
            selectLabel: "Valitse laskutettava yritys tai henkilö",
            selectHelper: "Laskutus tehdään valitsemallesi asiakkaalle tilauksen hyväksymisen jälkeen.",
            typeCompany: " · Yritys",
            typePrivate: " · Yksityinen",
            privateOption: "Laskuta yksityishenkilönä{name}",
            createNew: "+ Luo uusi yritys…",
            alreadyExists: "Yritys on jo listallasi — valittu sinulle.",
            ytunnusInvalid: "Anna y-tunnus muodossa 1234567-8",
            nameRequired: "Anna yrityksen nimi",
            nameTooLong: "Nimi on liian pitkä (max 250 merkkiä)",
            createFailed: "Yrityksen luonti epäonnistui",
            ytunnusLabel: "Y-tunnus",
            companyNameLabel: "Yrityksen nimi",
            saving: "Tallennetaan…",
            save: "Tallenna",
        },

        attachmentsSection: {
            title: "Liitteet",
            addLabel: "Lisää",
            helper: "esim. pumpun pystytyspaikka, valupaikka tai muotti — voit lisätä liitteitä myös myöhemmin",
            readyHint: "Liitteet ovat valinnaisia.",
            altFallback: "Liite",
            deleteAria: "Poista liite",
            deleteFailed: "Liitteen poisto epäonnistui",
            takePhoto: "Ota kuva",
            chooseImages: "Valitse kuvia",
            addPdf: "Lisää PDF",
        },

        attachmentsUpload: {
            noFileSelected: "Tiedostoa ei valittu",
            unsupportedType: "Tuettu vain JPG, PNG, WEBP tai PDF",
            imageTooLarge: "Tiedosto on liian suuri (max 50 MB)",
            fileTooLarge: "Tiedosto on liian suuri (max 20 MB)",
            sasFetchFailed: "SAS-tokenin haku epäonnistui",
            sasEmpty: "SAS-token tyhjä",
            azureUploadFailed: "Tiedoston lataus Azureen epäonnistui",
        },

        // rasitusluokat.js — SFS-EN 206 exposure-class assistant. `value`/`result`
        // wire strings and node/option `id`s are NOT here (data, not copy) — see
        // src/utils/quote/rasitusluokat.js. Key slugs mirror the source's own
        // node/option ids so the mapping is mechanical to audit.
        rasitusluokka: {
            groups: {
                common: "Yleisimmät",
                special: "Erikoistapaukset",
                other: "Muu",
            },
            presets: {
                unknown: { label: "En tiedä / valitse käyttökohteen perusteella" },
                x0: { label: "X0 — Raudoittamaton sisätila", meaning: "Ei raudoitusta eikä ympäristörasitusta — yksinkertaisin täyttöbetoni.", example: "Perustusten alustäyttö, kevyt täyttö lattian alle." },
                xc1: { label: "XC1 — Tavallinen sisätila, raudoitettu", meaning: "Normaali huoneilma, ei kosteutta tai raudoitusta ruostuttavia aineita.", example: "Asuinhuoneiden seinät, holvit ja välipohjat." },
                xc2: { label: "XC2 — Sokkeli / perustus (maan alla)", meaning: "Betoni on jatkuvasti kosteassa maaperässä, mutta se ei pääse jäätymään.", example: "Anturat, kellarin seinät, paalut." },
                xc34xf1: { label: "XC3-4, XF1 — Säänkestävä (pystypinta)", meaning: "Normaali ulkoilma ja sade, mutta vesi valuu pois pinnasta nopeasti.", example: "Rakennuksen sokkeli, ulkoseinät, pilarit ulkona." },
                xc34xf3: { label: "XC3-4, XF3 — Säänkestävä vaakapinta (parvekelaatta)", meaning: "Betoni on vaakatasossa, jolloin vesi ja lumi voivat jäädä pintaan 'makaamaan'.", example: "Parvekelaatat, ulkoportaat, pihakivetys." },
                xc3Suojattu: { label: "XC3 — Sateelta suojattu ulkorakenne", meaning: "Vaihtelevasti kostea ulkotila, mutta vesi ei pääse suoraan rakenteeseen.", example: "Katos, kuisti, kostea sisätila kuten pesuhuone." },
                xc4Altis: { label: "XC4 — Sateelle altis ulkorakenne", meaning: "Vaihtelevasti märkä ulkorakenne, mutta ei pakkasrasitusta.", example: "Suojaamaton ulkoseinä lämpimillä alueilla (etelä-Eurooppa)." },
                xd2: { label: "XD2 — Sisätilan märkä kloridirasitus", meaning: "Sisätila, jossa raudoitettu rakenne altistuu jatkuvalle vesiroiskeelle ja klorideille.", example: "Uimahallit, kosteat tehdastilat, allasrakenteet." },
                xc34xd1xf2: { label: "XC3-4, XD1, XF2 — Lievä pakkas-kloridi", meaning: "Betoni altistuu satunnaisesti suolaiselle vedelle (esim. autosta tippuva suola).", example: "Kotitalouden autotalli, pihatie, jota suolataan vähän." },
                xc34xd2xf2: { label: "XC3-4, XD2, XF2 — Pysäköintilaatta", meaning: "Pysäköintialueen tai -hallin lattia — säännöllinen kloridialtistus auton renkaista.", example: "Asuntoyhtiön parkkihalli, pihaparkki." },
                xc34xd3xf4: { label: "XC3-4, XD3, XF4 — Ankara pakkas-kloridi", meaning: "Betoni on jatkuvasti alttiina tiesuolalle ja suurelle vesimäärälle, joka jäätyy.", example: "Parkkihallit, tiesillat, valtaväylien reunakivet." },
                xc34xs1xf2: { label: "XC3-4, XS1, XF2 — Rannikkotalo (merivesi-ilmasto)", meaning: "Rakenne on lähellä merta (alle 1 km), ja suola kulkeutuu sumuna tai ilman mukana.", example: "Merenrannalla sijaitsevan talon julkisivu tai sokkeli." },
                xc34xf4xs3: { label: "XC3-4, XF4, XS3 — Merenrannalla (roiskealue)", meaning: "Betoni on suorassa kosketuksessa meriveteen tai aaltojen roiskeisiin.", example: "Laiturit, aallonmurtajat, rantamuuri." },
                xc2xs2: { label: "XC2, XS2 — Merivedenalainen", meaning: "Rakenne on jatkuvasti merivedessä, ei pakkasrasitusta.", example: "Merenpohjan paalut, merivedenalainen kellarin seinä." },
                xa2xc2: { label: "XA2, XC2 — Kemiallisesti rasitettu", meaning: "Maaperä tai pohjavesi on kemiallisesti rasittavaa (esim. sulfaatteja).", example: "Teollisuusalueen anturat, jätevedenpuhdistamon rakenteet." },
                muu: { label: "Muu (tarkenna kuvaukseen)" },
            },
            tree: {
                sisaUlko: {
                    question: "Onko kyseessä sisä- vai ulkorakenne?",
                    options: {
                        sisa: { label: "Sisärakenne", description: "Lämmitetty sisätila kuten asuinhuone, varasto tai pesula." },
                        ulko: { label: "Ulkorakenne", description: "Ulkona oleva tai lämmittämätön rakenne. Esim. sokkeli, parveke, piha, kylmä varasto." },
                    },
                },
                raudoitettu: {
                    question: "Onko raudoitettu rakenne?",
                    options: {
                        ei: { label: "Ei", description: "Pelkkä massabetoni ilman teräsraudoitusta. Esim. perustusten alustäyttö, matala anturalaatta." },
                        kylla: { label: "Kyllä", description: "Sisältää teräspalkit tai -verkon. Lähes kaikki rakenteelliset betonit (laatat, seinät, pilarit, palkit)." },
                    },
                },
                kosteustila: {
                    question: "Mikä on kosteustila?",
                    options: {
                        kuivaNormaaliSisa: { label: "Kuiva / normaali sisätila", description: "Normaali lämmitetty sisätila. Asuinhuoneet, toimistot, varastot, lämmitettyjen rakennusten eristetyt maavaraiset laatat, tavalliset vesieristetyt kylpyhuoneet (vesieristys pitää betonin kuivana)." },
                        vaativaKostea: { label: "Vaativa kostea tila", description: "Jatkuvasti tai usein kostea tila, jossa betonirakenne altistuu kosteudelle ilman vesieristystä. Esim. uimahalli, sauna, pesula, suurkeittiö, navetta, teollinen märkätila." },
                        kloridit: { label: "Kloridit", description: "Suolaa + kosteutta. Esim. autotallin lattia (tiesuolavesi), parkkihalli, ramppi, tiesilta, merenrantarakenteet, uimahalli." },
                    },
                },
                pakkasrasitus: {
                    question: "Onko pakkasrasitus?",
                    options: {
                        ei: { label: "Ei", description: "Rakenne pysyy maan alla, vedessä, lämpimässä tai sateelta suojassa — ei pääse jäätymään märkänä." },
                        kylla: { label: "Kyllä", description: "Rakenne kastuu ja jäätyy vuorotellen ulkona. Lähes kaikki suomalaiset ulkorakenteet (parveke, sokkeli, piha)." },
                    },
                },
                maanAllaTaiVedessa: {
                    question: "Onko rakenne maan alla tai vedessä?",
                    options: {
                        kylla: { label: "Kyllä", description: "Anturat, kellarin ulkoseinät, paalut, padot, pohjavedessä oleva rakenne." },
                        ei: { label: "Ei", description: "Maanpäällinen ulkorakenne kuten sokkeli, ulkoseinä, parveke tai piha." },
                    },
                },
                sateeltaSuojattu: {
                    question: "Onko rakenne sateelta suojattu?",
                    options: {
                        kylla: { label: "Kyllä", description: "Räystään tai katoksen alla, ei suoraa sadetta. Esim. kuisti, katoksen alla oleva pilari, ulkoporras katon alla." },
                        ei: { label: "Ei", description: "Suoraa sadetta saava rakenne. Esim. parveke, piha, suojaamaton ulkoseinä." },
                    },
                },
                kloridirasitusUlko: {
                    question: "Onko myös kloridirasitus?",
                    help: "Kloridi tarkoittaa suolaa — tiesuolaa tai merivettä. Suola syövyttää raudoituksen huomattavasti nopeammin kuin pelkkä vesi.",
                    options: {
                        ei: { label: "Ei", description: "Vain sade ja pakkanen, ei suolaa. Esim. tavallinen ulkoseinä, sokkeli kaukana tiestä, parveke." },
                        kylla: { label: "Kyllä", description: "Rakenne saa suolaa tiesuolasta tai merivedestä. Esim. autotalli, parkkihalli, tiesilta, rannikkotalo." },
                    },
                },
                pystyVaaka: {
                    question: "Pysty- vai vaakarakenne?",
                    options: {
                        pysty: { label: "Pysty", description: "Pystysuora pinta — vesi valuu pois nopeasti. Esim. seinä, sokkeli, pilari, kaide." },
                        vaaka: { label: "Vaaka", description: "Vaakapinta — vesi ja lumi jäävät pinnalle makaamaan. Esim. parveke, ulkoporras, pihalaatta." },
                    },
                },
                kloridiLahde: {
                    question: "Mikä on kloridin lähde?",
                    options: {
                        jaansulatussuola: { label: "Jäänsulatussuola", description: "Tiesuola: talvella käytettävä natriumkloridi. Sulamisvesi roiskuu rakenteisiin auton renkaista tai tien reunalta." },
                        merivesi: { label: "Merivesi", description: "Suolavesi tai meri-ilmasto. Suola kulkeutuu sumuna ilman mukana (alle 1 km rannasta) tai osuu suoraan (laituri, aallonmurtaja)." },
                    },
                },
                xdVoimakkuus: {
                    question: "Pakkas- ja kloridirasituksen voimakkuus?",
                    options: {
                        lieva: { label: "Lievä / satunnainen", description: "Suolaa tulee harvoin tai vain ilman mukana. Esim. kotitalouden autotallin lattia (talvella renkaista valuva sulamisvesi)." },
                        ankara: { label: "Ankara / suora", description: "Suolaa tulee jatkuvasti tai suorana suihkuna. Esim. parkkihalli, tiesilta, ramppi, tien välitön läheisyys." },
                    },
                },
                xsSijainti: {
                    question: "Sijainti rannikolla?",
                    options: {
                        ilmaSuola: { label: "Ilman suola", description: "Rannikolla mutta ei aaltojen ulottuvilla. Esim. merenrannalla (alle 1 km) sijaitsevan talon julkisivu." },
                        roiskealue: { label: "Roiskealue", description: "Suorassa kosketuksessa veteen tai aaltojen roiskeisiin. Esim. laituri, aallonmurtaja, rantamuuri." },
                        vedenAlla: { label: "Jatkuvasti veden alla", description: "Merivedenalainen osa, ei roiske- tai vaihteluvyöhykettä. Esim. merenpohjan paalut, merivedenalainen kellarin seinä. Cover vaatii rakennesuunnittelijan tarkistuksen." },
                    },
                },
            },
            terminals: {
                x0: { resultLabel: "X0 — Ei korroosio- tai syöpymisriskiä", description: "Ei riskiä" },
                xc1: { resultLabel: "XC1 — Tavallinen sisätila, raudoitettu", description: "Asuinhuoneet" },
                xc3Sisa: { resultLabel: "XC3 — Kostea sisätila", description: "Pesutilat, kostea" },
                xd2: { resultLabel: "XD2 — Märkä kloridirasitus", description: "Uimahallit tms." },
                xc2: { resultLabel: "XC2 — Sokkeli / perustus (maan alla)", description: "Anturat, perustukset" },
                xc3u: { resultLabel: "XC3 — Sateelta suojattu ulkorakenne", description: "Ulko, katettu tila" },
                xc4: { resultLabel: "XC4 — Sateelle altis ulkorakenne", description: "Suojaamaton pysty" },
                xf1: { resultLabel: "XC3-4, XF1 — Säänkestävä (pystypinta)", description: "Sokkeli, seinä" },
                xf3: { resultLabel: "XC3-4, XF3 — Säänkestävä vaakapinta", description: "Parveke, portaat" },
                xd1: { resultLabel: "XC3-4, XD1, XF2 — Lievä pakkas-kloridi (autotalli)", description: "Autotalli" },
                xd3: { resultLabel: "XC3-4, XD3, XF4 — Ankara pakkas-kloridi", description: "Silta, P-talo" },
                xs1: { resultLabel: "XC3-4, XS1, XF2 — Rannikkotalo (merivesi-ilmasto)", description: "Rannikkotalo" },
                xs2: { resultLabel: "XC2, XS2 — Merivedenalainen", description: "Merenpohjan paalut" },
                xs3: { resultLabel: "XC3-4, XS3, XF4 — Merenrannalla (roiskealue)", description: "Laituri" },
            },
        },

        // kayttokohdePresets.js — the two käyttökohde-specific post-questions
        // (anturat + lattia). Option/node `id`s and `override` targets are data,
        // not here — see src/utils/quote/kayttokohdePresets.js.
        kayttokohdePresets: {
            anturat: {
                question: "Jääkö antura tai sen osa säälle/pakkaselle alttiiksi?",
                eiSaalleAltisLabel: "Ei, normaalisti maan alla",
                kyllaSaalleAltisLabel: "Kyllä, matala tai osin säälle altis",
                kyllaSaalleAltisDescription: "Tiukennetaan rasitusluokkaan XF1 (pystypinta, pakkasrasitus).",
            },
            lattia: {
                question: "Altistuuko laatta jäänsulatussuolalle (esim. autotalli)?",
                eiLabel: "Ei",
                kyllaLabel: "Kyllä",
                kyllaDescription: "Valitsemme tiukemman XD1-luokan ja viimeistelyä helpottavan koostumuksen.",
            },
        },

        // quoteConstants.js — stepLabels/betoniLaatuDisclaimer are wizard-only. The
        // options.* below back the 5 KAYTTOKOHDE/LUJUUSLUOKKA/RAEKOKO/NOTKEUS/
        // KAYTTO_IKA_OPTIONS functions — deliberately kept here (not under a
        // customer/provider namespace) because their consumers span three
        // extraction tasks: StepReview.jsx/StepConcrete.jsx/KayttokohdeField.jsx/
        // SpecGuideModal.jsx/betoniLaatuSelvitys.js (16, already extracted),
        // ValuDetail.jsx/ValuListCard.jsx (18), RequestRecap.jsx/
        // TarjouspyyntoListCard.jsx (19, not yet extracted — see task-18-report.md
        // for how those two files were threaded without extracting their own
        // strings). Only `value`/`typicalClass` stay untouched (wire data); grade
        // codes ("C20/25", "S2", "16 mm", …) are international notation, not
        // language, and are NOT translated — only the "En tiedä"/"Muu" sentinels
        // and the käyttökohde labels/descriptions go through `t()`.
        quoteConstants: {
            betoniLaatuDisclaimer: "Tämä on arvio tilausta varten. Jos kohteella on rakennesuunnitelmat, käytä aina niissä määrättyä betonilaatua.",
            stepLabels: {
                osoite: "Osoite",
                pumppu: "Pumppu",
                betoni: "Betoni",
                lisatiedot: "Lisätiedot",
                tarkista: "Tarkista",
            },
            options: {
                kayttokohde: {
                    anturat: {
                        label: "Anturat",
                        description: "Rakennuksen perustusten anturat. Suunnittelija voi vaihtoehtoisesti hyväksyä myös XC2 tai XC1, XC2 -yhdistelmän.",
                    },
                    sokkeli: {
                        label: "Sokkeli",
                        description: "Sokkeli ja maanvaraisen ulkoseinän alaosa — sade- ja pakkasrasitus.",
                    },
                    harkko: {
                        label: "Harkko",
                        description: "Harkkomuurin täyttöbetoni — juokseva massa pumpataan harkkojen kennoon.",
                    },
                    lattia: {
                        label: "Lattia tai laatta",
                        description: "Maanvarainen laatta tai sisätilan lattia. Autotalli kysytään erikseen avustimessa.",
                    },
                    seinat: {
                        label: "Seinät",
                        description: "Sisätilan kantavat tai puoliksi kantavat seinät.",
                    },
                    holvi: {
                        label: "Holvi / välipohja",
                        description: "Yläpohjan tai välipohjan kantava laatta.",
                    },
                    pilarit: {
                        label: "Pilarit",
                        description: "Sisätilan pylväät ja kantavat pilarit.",
                    },
                    portaat: {
                        label: "Portaat",
                        description: "Sisä- tai ulkotilan betoniportaat. Avustin kysyy sijainnin.",
                    },
                    muu: {
                        label: "Muu",
                        description: "Muu käyttökohde — tarkenna kuvauskentässä.",
                    },
                },
                lujuusluokka: {
                    unknown: "En tiedä",
                    muu: "Muu",
                },
                raekoko: {
                    unknown: "En tiedä",
                    hieno16: "16 mm hieno",
                    muu: "Muu",
                },
                notkeus: {
                    unknown: "En tiedä",
                    muu: "Muu",
                },
                kayttoIka: {
                    v50: "50v",
                    v100: "100v",
                },
            },
        },
    },
    attachments: {
        uploadFailed: "Lataus epäonnistui",
    },
    jerry: {
        applyFailed: "Lähetys epäonnistui",
    },

    // Task 18 — landing page + shared marketing sections, /tilaa page shell,
    // /laskuri page shell, 404, cookie consent.
    landing: {
        hero: {
            microcopy: {
                fast: "Nopea",
                free: "Täysin ilmainen",
                noCommitment: "Ei sitoumuksia",
            },
            taglineLead: "Betonipumppaus: ",
            taglineAccent: "yksi pyyntö, useita tarjouksia.",
            taglineSub: "Betoni ja pumppaus samalla pyynnöllä – ilman soittokierroksia.",
            mascotAlt: "Jerry, betonipumppuoperaattori",
            greeting: "Tervetuloa, {name}.",
        },
        process: {
            heading: "Näin se toimii",
            subheading: "Kolme yksinkertaista vaihetta tarjouksiin ja pumppauspäivään",
            steps: {
                step1: { title: "Jätä ilmainen tarjouspyyntö", body: "Kerro työmaasi tiedot: osoite, betoni, ajankohta ja puomin tarve. Vie alle 2 minuuttia." },
                step2: { title: "Vertaile tarjouksia", body: "Pumppaajat ottavat sinuun yhteyttä tarjouksillaan. Vertaile hintoja ja aikatauluja." },
                step3: { title: "Valitse paras ja varaa pumppaus", body: "Hyväksyt sopivan tarjouksen suoraan pumppaajan kanssa. Ei välityspalkkioita, ei sitoumuksia." },
            },
        },
        leadGen: {
            heading: "Miksi Betonijerry?",
            saveTime: { title: "Säästä aikaa", body: "Yksi pyyntö tavoittaa useita pumppaajia. Et soittele — pumppaajat tarjoavat sinulle." },
            network: { title: "Kattava verkosto" },
            trusted: { title: "Luotettavat pumppaajat", body: "Listalla olevat pumppaajat ovat ammattilaisia, joilla on tarvittavat luvat ja vakuutukset." },
        },
        requestForm: {
            pageTitle: "Pyydä tarjous betonipumppauksesta — BetoniJerry",
            pageDescription: "Lähetä yksi tarjouspyyntö ja vertaile useita tarjouksia betonipumppauksesta. Ilmainen ja sitoumukseton — vie alle 2 minuuttia.",
            waitingForAttachments: "Odota, että liitteet latautuvat loppuun.",
            confirmBillingToContinue: "Vahvista laskutusvalinta jatkaaksesi.",
            floorPlanSnapshotComment: "Betonilaskurin pohjakuva",
        },
        notFound: {
            pageTitle: "Sivua ei löytynyt — BetoniJerry",
            heading: "Hups — tätä sivua ei löytynyt. Linkki saattaa olla vanhentunut tai virheellinen.",
            cta: "Takaisin etusivulle",
        },
        cookieConsent: {
            ariaLabel: "Evästeasetukset",
            intro: "Betonijerry ei käytä evästeitä omaan toimintaansa.",
            body: "Käytämme kuitenkin Googlen mainontaa ja kävijämittausta, jotka voivat suostumuksellasi tallentaa evästeitä. {link}",
            readMore: "Lue lisää",
            reject: "Hylkää",
            accept: "Hyväksy",
        },
    },

    // Task 18 — /valut (customer's own pours: list, detail, offer cards, accept flow).
    valut: {
        shared: {
            newRequestCta: "Pyydä uusi tarjouspyyntö →",
        },
        status: {
            draft: "Luonnos",
            open: "Avoin",
            pendingVerification: "Tarkistuksessa",
            accepted: "Hyväksytty",
            cancelled: "Peruutettu",
            expired: "Vanhentunut",
            noSupply: "Ei alueellasi",
        },
        myValutPage: {
            pageTitle: "Omat valut — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            intro: "Lähettämäsi tarjouspyynnöt ja niihin saadut tarjoukset.",
            tabs: {
                avoimet: "Avoimet",
                hyvaksytyt: "Hyväksytyt",
                peruuntuneet: "Peruuntuneet",
                eiAlueellasi: "Ei vielä alueellasi",
            },
            noSupplyNotice: "Saat sähköpostia kun palvelu laajenee alueellesi.",
            loadingAriaLabel: "Ladataan tarjouspyyntöjä…",
            loadFailed: "Tarjouspyyntöjen lataus epäonnistui. Yritä uudelleen.",
            emptyOpen: "Ei avoimia tarjouspyyntöjä. Pyydä uusi tarjous — vastaukset tulevat tähän.",
            emptyAccepted: "Ei hyväksyttyjä valuja vielä.",
            emptyCancelled: "Ei peruuntuneita pyyntöjä.",
            emptyNoSupply: "Ei pyyntöjä alueilta, joilla ei vielä ole palveluntarjoajia.",
        },
        listCard: {
            mapAlt: "Karttakuva osoitteesta {address}",
            mapAltUnavailable: "Karttakuva ei saatavilla",
            sentPrefix: "Lähetetty {date}",
            acceptedPrefix: "Hyväksytty: {provider}",
            offerCountLabel: "Tarjouksia {count}",
            boomFromRear: "Perästä pumppaus",
            boomSuffix: "{m} m puomi",
            linjaSuffix: "{m} m linja",
            viewCta: "Katso →",
        },
        detail: {
            pageTitle: "Valupyyntö: {address} — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            pushToastDefaultTitle: "Uusi tarjous saapui",
            notFound: "Tarjouspyyntöä ei löydy.",
            backToMyValut: "← Takaisin omiin valuihin",
            recapHeading: "Pyyntösi yhteenveto",
            addressLabel: "Osoite",
            kayttokohdeLabel: "Käyttökohde",
            amountLabel: "Määrä",
            rasitusluokkaLabel: "Rasitusluokka",
            boomLabel: "Puomi",
            boomFromRear: "Perästä pumppaus",
            linjaLabel: "Linja",
            driveLabel: "Ajo-ohjeet",
            detailsLabel: "Lisätiedot",
            offersHeading: "Tarjoukset ({count})",
            validUntil: "Tarjouspyyntö on voimassa {date} asti",
            waitingForOffers: "Odotamme tarjouksia — saat ilmoituksen sähköpostitse heti kun ensimmäinen tarjous saapuu.",
            subscribePromptLabel: "Ilmoita selaimeen kun tarjous saapuu",
            subscribeSuccessLabel: "Ilmoitukset päällä tällä laitteella",
            subscribeHelperText: "Saat ilmoituksen selaimeen heti kun pumppausyritys lähettää tarjouksen.",
            viewersHeading: "Nämä yritykset ovat tutustuneet pyyntöösi ({count})",
            viewersHelper: "Yritys voi olla sinuun suoraan yhteydessä tai jättää tarjouksen tässä palvelussa.",
            declinedHeading: "Kieltäytyneet pumppausyritykset ({count})",
            declinedHelper: "Nämä yritykset eivät tee tarjousta tähän pyyntöön.",
        },
        offerCard: {
            showLess: "Näytä vähemmän",
            readMoreIntro: "Lisää…",
            priceEstimateLabel: "Hinta-arvio",
            priceAriaLabel: "Hinta-arvio {total}, sisältää arvonlisäveron {vat} prosenttia",
            vatLine: "ALV {vat} % sis. · ALV 0 %: {netto}",
            unitPriceLine: "≈ {unitPrice} (arvio, alv 0 %)",
            priceTermsLabel: "Mitä hintaan sisältyy",
            locationLabel: "Sijainti",
            locationValue: "{dist} kohteesta",
            validUntilLabel: "Tarjous voimassa",
            validUntilValue: "{date} asti",
            cancellationLabel: "Peruutusehdot",
            cancellationHint: "Lue lisää alta",
            readMoreExpand: "▾ Lue lisää tarjouksesta",
            readMoreCollapse: "▴ Sulje",
            extraNotesLabel: "Lisätiedot",
            askViaChat: "Kysy viesteillä",
            chatTitle: "Viestit · {name}",
            acceptedNotice: "Tämä tarjous on hyväksytty {date}",
        },
        acceptConfirmModal: {
            providerFieldValue: "{name} (Y-tunnus {ytunnus})",
            totalWithVat: "{total} (sis. ALV {vat} %)",
            validUntilValue: "{date} asti",
            cancel: "Peruuta",
        },
        acceptedScreen: {
            heading: "Onneksi olkoon, valitsit pumppaajan!",
            contactNamed: "{name} ottaa sinuun yhteyttä ja vahvistaa pumppausajan.",
            contactGeneric: "Pumppaaja ottaa sinuun yhteyttä ja vahvistaa pumppausajan.",
            contactPersonLabel: "Yhteyshenkilö: {name}",
            phoneLabel: "Puhelin:",
            hoursLabel: "Aukioloajat: {hours}",
            backToMyValut: "Takaisin omiin valuihin",
        },
        progress: {
            step1Title: "Saat tarjouksia",
            step1Sub: "Vertaile hintoja ja aikatauluja rauhassa",
            step2Title: "Valitse pumppufirma",
            step2Sub: "yhteydenotto ei sido sinua mihinkään",
            step3Title: "Vahvista pumppausaika",
            step3Sub: "sovitaan tarkennukset puhelimitse hyväksynnän jälkeen",
        },
    },

    // Task 18 — /laskuri page + the shared FloorPlanCalculator (also embedded in
    // the wizard's Betoni step, hence "laskuri.calculator" not "wizard.*").
    laskuri: {
        page: {
            pageTitle: "Betonilaskuri lattiavaluun — laske betonin menekki (m³) | BetoniJerry",
            pageDescription: "Ilmainen visuaalinen betonilaskuri lattiavaluun: piirrä pohjakuva, aseta laatan paksuus ja kaadot — saat tarkan betonimäärän (m³) ja tilaussuosituksen.",
            calculatorLoadFailed: "Laskurin lataus epäonnistui. Päivitä sivu ja yritä uudelleen.",
            heading: "Betonilaskuri lattiavaluun",
            intro: "Piirrä lattiasi pohjakuva, aseta laatan paksuus ja lattiakaivojen kaadot — laskuri kertoo tarkan betonimäärän ja suosituksen tilattavasta määrästä. Ilmainen, ei vaadi kirjautumista.",
            useInQuoteCta: "Pyydä tarjoukset tällä määrällä",
            howComposedHeading: "Mistä betonin menekki muodostuu?",
            howComposedBody1: "Lattiavalun betonimäärä on pinta-ala kertaa paksuus — mutta todellisuudessa laatan paksuus vaihtelee, märkätiloissa on kaadot lattiakaivoihin ja pohjakuva on harvoin suorakulmio. Tämä laskuri laskee tilavuuden suoraan piirtämästäsi pohjakuvasta: alueet voivat olla limittäin (päällekkäistä alaa ei lasketa kahdesti), paksuuden voi asettaa kulmittain ja kaivojen kaadot vähennetään automaattisesti.",
            howComposedBody2: "Kun määrä on selvillä, voit pyytää samalla pohjakuvalla tarjoukset betonipumppauksesta — yksi pyyntö tavoittaa alueesi pumppaajat.",
            faqHeading: "Usein kysyttyä",
            faq: {
                demand: { q: "Paljonko betonia lattiavaluun tarvitaan?", a: "Tarve on pinta-ala kertaa laatan paksuus. Esimerkiksi 20 m² lattia 100 mm laatalla on 2,0 m³. Laskuri huomioi myös kaadot, eri paksuudet ja epäsäännölliset muodot, ja lisää hukkavaran." },
                wasteAllowance: { q: "Mikä on hukkavara ja miksi sitä tarvitaan?", a: "Alusta ei ole koskaan täysin tasainen, joten betonia kuluu aina laskennallista enemmän. Yleinen hukkavara on 5–10 %. Loppuminen kesken valun on kallista — tilaa mieluummin hieman yli." },
                drainSlope: { q: "Miten kaato lattiakaivoon vaikuttaa betonin määrään?", a: "Kaato ohentaa laattaa kaivon ympärillä, joten betonia kuluu hieman vähemmän. Laskuri vähentää kaadon tilavuuden automaattisesti, kun lisäät kaivon ja määrität kaadon syvyyden ja säteen." },
            },
        },
        calculator: {
            useAmountDefault: "Käytä määrä",
        },
        toolbar: {
            addRoomRect: "Lisää huone (suorakulmio)",
            addRoomTri: "Lisää huone (kolmio)",
            addRoomCircle: "Lisää huone (ympyrä)",
            addPillar: "Lisää pilari",
            addDrain: "Lisää lattiakaivo",
            presets: "Mallipohjat",
            fitView: "Sovita näkymä",
            undo: "Kumoa",
            clearAll: "Tyhjennä kaikki",
            viewToggleAriaLabel: "Näkymä (2D / 3D)",
            view2dAriaLabel: "2D-näkymä",
            view3dAriaLabel: "3D-näkymä",
            exitFullscreenTooltip: "Poistu koko näytöstä (Esc)",
            exitFullscreenAriaLabel: "Poistu koko näytöstä",
            fullscreen: "Koko näyttö",
        },
        drainPanel: {
            heading: "Lattiakaivo ja kaato",
            close: "Sulje",
            depthLabel: "Kaadon syvyys",
            radiusLabel: "Kaadon säde",
            ratioHint: "Kaato ≈ 1:{ratio} — märkätiloissa suositus yleensä 1:80–1:100, kaivon vieressä jyrkempi.",
            outsidePourWarning: "Kaivo ei ole valetulla alueella — se ei vaikuta määrään.",
            exceedsThicknessWarning: "Kaato ylittää laatan paksuuden ({mm} mm) — betoni ei riitä kaadon pohjalla.",
            deleteDrain: "Poista kaivo",
        },
        shapePanel: {
            close: "Sulje",
            cornerLabelsRect: { nw: "Vasen ylä", ne: "Oikea ylä", se: "Oikea ala", sw: "Vasen ala" },
            cornerLabelsTri: { c1: "Kulma 1", c2: "Kulma 2", c3: "Kulma 3" },
            typeLabels: { rect: "Suorakulmio", tri: "Kolmio", circle: "Ympyrä", pillar: "Pilari" },
            widthLabel: "Leveys",
            lengthLabel: "Pituus",
            diameterLabel: "Halkaisija",
            heightLabel: "Korkeus",
            uniformThickness: "Sama paksuus",
            perCornerThickness: "Kulmittain",
            thicknessLabel: "Paksuus",
            duplicate: "Monista",
            delete: "Poista",
            circleThicknessNote: "Ympyrälle paksuus on aina sama koko alueella.",
            pillarNote: "Pilari lasketaan lieriönä ja lisätään betonimäärään. Se ei sisälly lattian pinta-alaan.",
        },
        resultPanel: {
            areaLabel: "Pinta-ala",
            exactLabel: "Betoni",
            recommendedLabel: "Suositus",
            decreaseWaste: "Pienennä hukkavaraa",
            wasteAllowanceLabel: "Hukkavara {pct} %",
            increaseWaste: "Kasvata hukkavaraa",
            computeError: "Laskenta epäonnistui — näytetään viimeisin onnistunut tulos.",
        },
        // Used inline by planModel.js's floorPlanSummary() — a pure module with no
        // bundle access, so the caller resolves this word via t() and passes it in.
        floorPlanSummary: {
            recommendedWord: "suositus",
        },
        presetPicker: {
            title: "Aloita pohjasta",
            emptyPlan: "Tyhjä pohja",
            presets: {
                autotalli: { name: "Autotalli", description: "Yhden auton talli 6,0 × 3,6 m, laatta 100 mm" },
                kylpyhuone: { name: "Kylpyhuone + sauna", description: "Pesuhuone ja sauna, 80 mm laatta, kaadot kaivoihin" },
                terassi: { name: "Terassilaatta", description: "Viistetty terassi, 80 mm laatta" },
            },
        },
        plan3dView: {
            noWebgl: "Selaimesi ei tue 3D-näkymää (WebGL puuttuu). Käytä 2D-näkymää.",
        },
        planCanvas: {
            zoomIn: "Lähennä",
            zoomOut: "Loitonna",
        },
    },

    // Task 18 — /kayttoehdot, /tietosuoja page shells + legally load-bearing
    // acceptance-copy fallbacks from src/utils/legal/acceptanceCopy.js (rendered
    // only when the DB fetch/parse fails — see betonijerry/CLAUDE.md "Legal /
    // acceptance model"). GATE1_FALLBACK_VERSION stays a plain unlocalized
    // constant in code — it's a version identifier, not copy.
    legal: {
        shared: {
            versionLabel: "Versio {version}",
            effectiveDateSuffix: " · voimassa {date}",
        },
        kayttoehdot: {
            pageTitle: "Käyttöehdot — BetoniJerry",
            pageDescription: "BetoniJerryn käyttöehdot. BetoniJerry on PumiNet Oy:n betonipumppauksen tarjouspyyntöpalvelu.",
            heading: "Käyttöehdot",
            loadFailed: "Käyttöehtojen lataaminen epäonnistui. Yritä päivittää sivu hetken kuluttua.",
        },
        tietosuoja: {
            pageTitle: "Tietosuojaseloste — BetoniJerry",
            pageDescription: "BetoniJerryn tietosuojaseloste — miten henkilötietojasi käsitellään. Rekisterinpitäjä PumiNet Oy.",
            heading: "Tietosuojaseloste",
            loadFailed: "Tietosuojaselosteen lataaminen epäonnistui. Yritä päivittää sivu hetken kuluttua.",
        },
        gate1Fallback: {
            checkboxLabel:
                "Hyväksyn, että Betonijerry välittää tarjouspyyntöni ja yhteystietoni valituille betonipumppausyrityksille tarjousten saamiseksi. " +
                "Vakuutan, että antamani työmaatiedot ovat parhaan tietoni mukaan oikein. " +
                "Ymmärrän, että tarjouspyynnön lähettäminen ei vielä sido minua tilaamaan palvelua.",
            details: {
                marketplace: "Betonijerry on tarjouspyyntö- ja markkinapaikkapalvelu. Betonijerry ei itse suorita betonipumppausta eikä myy betonia. Mahdollinen palvelusopimus syntyy aina sinun ja valitsemasi palveluntarjoajan välille.",
                accuracy: "Vastaat siitä, että antamasi osoite-, määrä-, betoni- ja työmaatiedot ovat oikeita ja että työmaalle on turvallinen pääsy ja pumpulle soveltuva pystytyspaikka. Palveluntarjoaja voi tarkentaa tarjousta tai kieltäytyä työstä, jos olosuhteet poikkeavat olennaisesti ilmoitetusta.",
                leftoverAmount: "Tilattava betonimäärä on aina pumpattavaa määrää suurempi: osa jokaisesta kuormasta jää pumppauskalustoon (puomiin ja linjaan) eikä päädy valuun, ja määrä pyöristetään ylöspäin 0,5 m³:n portain. Ylijäävä betoni on tilaajan vastuulla — se jää työmaalle tai toimitetaan kierrätykseen erillistä maksua vastaan.",
                noStructuralResponsibility: "Betonijerry ei vastaa rakennesuunnittelusta, betonilaadun teknisestä soveltuvuudesta, rasitusluokan tai lujuusluokan valinnasta. Jos kohteessa on suunnitelmat, noudata suunnittelijan tai vastaavan työnjohtajan ohjeita.",
                dataProcessing: "Käsittelemme tarjouspyynnön tiedot ja yhteystietosi tarjousten välittämistä ja asiakaspalvelua varten. Tarkemmat tiedot henkilötietojen käsittelystä on kuvattu erikseen tietosuojaselosteessamme.",
                contactVisibility: "Pyynnön vastaanottavat pumppausyritykset näkevät yhteystietosi (nimi, osoite, puhelin, sähköposti) kirjauduttuaan palveluun, ja voivat olla sinuun suoraan yhteydessä. Saat ilmoituksen sähköpostitse, kun yritys tutustuu pyyntöösi.",
                offerOrdering: "Tarjousten näkyminen ja järjestys voi perustua mm. sijaintiin, kaluston soveltuvuuteen, saatavuuteen ja vasteaikaan.",
            },
        },
        peruutusehdotStandard: {
            summary: "Maksuton peruutus edellisenä työpäivänä klo 12 asti. Peruuttamattomasta tilauksesta palveluntarjoaja veloittaa 240 €.",
            full:
                "Pumpun tilauksen peruutus tulee tehdä viimeistään edellisenä työpäivänä klo 12:00 mennessä. "
                + "Peruuttamattomasta tilauksesta palveluntarjoaja veloittaa 240 €. "
                + "Mikäli lisäaineet on ehditty tilata ennen pumpun tilauksen peruutusta, palveluntarjoaja veloittaa myös kyseiset lisäaineet. "
                + "Peruutusmaksu on valitsemasi palveluntarjoajan sopimusehto; Betonijerry ei ole maksun osapuoli. "
                + "Tämä ei rajoita kuluttajan kuluttajansuojalain mukaisia oikeuksia.",
        },
        gate2Fallback: {
            ctaLabel: "HYVÄKSY TARJOUS",
            ctaCaption: "Sitova tilaus ja pumppausaika sovitaan puhelimitse hyväksynnän jälkeen",
            ariaLabel: "Hyväksy tarjous, sitova tilaus ja pumppausaika sovitaan puhelimitse hyväksynnän jälkeen",
            confirmTitle: "Vahvista valinta",
            responsibilityLabel: "Tilaajan vastuulla",
            responsibilityBody: "Sitova tilaus ja pumppausaika sovitaan puhelimitse hyväksynnän jälkeen.",
            fieldLabels: {
                provider: "Palveluntarjoaja",
                total: "Kokonaishinta",
                cancellation: "Peruutusehdot",
                validUntil: "Voimassa",
            },
        },
    },

    // /yhteystiedot. Names, addresses and numbers are NOT here — they are
    // constants in the page component (identifiers, not translatable copy).
    contact: {
        pageTitle: "Yhteystiedot — BetoniJerry",
        pageDescription:
            "BetoniJerryn yhteystiedot: nimetty yhteyshenkilö, puhelin ja sähköposti. Palvelun tarjoaa {operator} (Y-tunnus {businessId}).",
        heading: "Yhteystiedot",
        lead: "Kysyttävää tarjouspyynnöstä, saamastasi tarjouksesta tai palvelusta? Soita tai laita sähköpostia — vastaamme arkisin.",
        personLabel: "Yhteyshenkilö",
        phoneLabel: "Puhelin",
        emailLabel: "Sähköposti",
        generalLabel: "Yleiset asiat",
        requestHint: "Jos asia koskee jo lähetettyä tarjouspyyntöä, mainitse viestissä työmaan osoite ja valupäivä — löydämme pyynnön nopeammin.",
        privacyLabel: "Tietosuoja-asiat",
        operatorLabel: "Palveluntarjoaja",
        operatorValue: "{operator} · Y-tunnus {businessId}",
        operatorNote: "BetoniJerry välittää tarjouspyynnöt alueen betonipumppausyrityksille. BetoniJerry ei itse suorita betonipumppausta eikä myy betonia.",
    },

    // Task 19 — provider-facing pages: /tarjouspyynnot (list + detail), the
    // public preview link, the offer form, /hallinta (vehicles/people/sijainnit
    // admin), /tuki (support threads) and /liity (self-onboarding). "shared"
    // holds the handful of words/phrases genuinely reused across ≥2 files in
    // this scope; everything else is page/component-scoped even where two
    // pages happen to render byte-identical Finnish (matches the valut.*
    // pageTitleFallback precedent from Task 18 — duplication kept explicit
    // rather than coupling unrelated pages' future copy).
    provider: {
        shared: {
            cancel: "Peruuta",
            edit: "Muokkaa",
            declineCta: "En tarjoa tähän",
            priceLabel: "Hinta (€)",
            priceHelper: "Veroton hinta — ALV 25,5 % lisätään asiakkaan laskulle",
            // Short label, long example in the helper: the old 50-character label
            // overflowed its own field by 64px at desktop width.
            extraNotesLabel: "Lisätiedot (valinnainen)",
            extraNotesHelper: "Esim. betonin laatu, toimitus, työaika",
            companyType: "Yritys",
            privateType: "Yksityinen",
        },
        detail: {
            pageTitle: "Tarjouspyyntö: {address} — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            providersOnlyNotice: "Tarjouspyynnöt on tarkoitettu pumppausyrityksille.",
            notFound: "Tarjouspyyntöä ei löydy.",
            backToRequests: "← Takaisin tarjouspyyntöihin",
            backToList: "← Tarjouspyynnöt",
            loadFailed: "Lataus epäonnistui. Yritä uudelleen.",
            requestIdLabel: "Tarjouspyyntö #{id}",
            confirmScheduleCta: "Vahvista ja aikatauluta betoni.onlinessa →",
            wonNotice: "Asiakas hyväksyi tarjouksesi!",
            yourOfferHeading: "Tarjouksesi",
            declinedNotice: "Kieltäydyit tästä tarjouspyynnöstä.",
            declinedReasonSuffix: " Perustelu: {reason}",
            undeclineCta: "Peruuta kieltäytyminen ja tee tarjous",
            endedNoOfferNotice: "Tämä tarjouspyyntö on päättynyt.",
            contactSupportCta: "Ota yhteyttä ylläpitoon",
            chatHeading: "Asiakasviestintä (chat)",
            chatPreOfferHint: "Voit kysyä asiakkaalta tarkennuksia jo ennen tarjouksen tekemistä.",
            askCustomerCta: "Kysy asiakkaalta",
            supportDialogTitle: "Ylläpito",
            supportIntro: "Kirjoita viestisi ylläpidolle. Viesti lähetetään vasta kun painat Lähetä.",
            supportPlaceholder: "Kuvaile, missä tarvitset ylläpidon apua…",
            sendToSupportCta: "Lähetä ylläpidolle",
            supportThreadOpenFailed: "Ylläpidon viestiketjua ei saatu avattua. Yritä uudelleen.",
            supportContactFailed: "Yhteydenotto ylläpitoon epäonnistui. Yritä uudelleen.",
            declineDialogIntro: "Ilmoitamme asiakkaalle, ettet tee tarjousta. Voit halutessasi kertoa syyn (näkyy asiakkaalle).",
            declineReasonPlaceholder: "Esim. kalusto varattu kyseiselle päivälle (vapaaehtoinen)",
            confirmDeclineCta: "Vahvista: en tarjoa",
            declineFailed: "Kieltäytyminen epäonnistui. Yritä uudelleen.",
            withdrawCta: "Peru tarjous",
            withdrawDialogIntro: "Tarjous poistuu asiakkaan valittavista ja siirtyy Päättyneisiin. Perumista ei voi kumota.",
            confirmWithdrawCta: "Vahvista: peru tarjous",
            withdrawFailed: "Tarjouksen peruminen epäonnistui. Yritä uudelleen.",
        },
        preview: {
            deniedText:
                "Sinulla ei ole vielä käyttöoikeutta tähän tarjouspyyntöön. "
                + "Ota yhteyttä yrityksesi pääkäyttäjään tai BetoniJerry-tukeen.",
            deniedSignedInAs:
                "Olet kirjautuneena tunnuksella {email}. Jos yritykselläsi on jo tunnus "
                + "BetoniJerryyn tai betoni.onlineen, kirjaudu sillä.",
            switchAccountCta: "Kirjaudu toisella tunnuksella",
            unavailableText:
                "Tämä tarjouspyyntö ei ole enää yrityksesi käytettävissä — "
                + "se on voitu lähettää uudelleen toisille palveluntarjoajille.",
            pageTitle: "Tarjouspyynnön esikatselu — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            invalidLinkNotice: "Linkki ei kelpaa. Tarkista sähköpostisi uusin viesti tai kirjaudu sisään.",
            invalidLinkAuthenticatedNotice: "Linkki ei kelpaa. Tarkista sähköpostisi uusin viesti tai avaa tarjouspyynnöt sovelluksessa.",
            loginCta: "Kirjaudu sisään",
            openRequestsCta: "Avaa tarjouspyynnöt",
            expiredAuthenticatedNotice: "Linkki on vanhentunut, mutta voit avata tarjouspyynnön kirjautuneena.",
            expiredAnonNotice: "Linkki on vanhentunut. Kirjaudu sisään nähdäksesi tarjouspyynnön.",
            openRequestCta: "Avaa tarjouspyyntö",
            loadingText: "Haetaan tarjouspyyntöä…",
            // The heading names the job, not the React route ("esikatselu").
            heading: "Tarjouspyyntö #{id}",
            summaryAmount: "{m3} m³",
            summaryDuration: "{h} h",
            attachmentsRevealedHint: "Asiakkaan liitteet näkyvät kirjautuneena.",
            revealCta: "Ilmaise kiinnostus",
            revealHint: "Ilmoitamme asiakkaalle, että yrityksesi on kiinnostunut. Et sitoudu mihinkään.",
            interestSentNotice: "Kiitos — asiakkaalle on ilmoitettu kiinnostuksestasi.",
            genericRetryError: "Jokin meni pieleen. Yritä hetken kuluttua uudelleen.",
            requestEndedNotice: "Tarjouspyyntö on päättynyt.",
            alreadyOfferedNotice: "Yrityksesi on jo tehnyt tarjouksen tähän tarjouspyyntöön.",
            loginViewOfferCta: "Kirjaudu ja katso tarjouksesi",
            viewOfferCta: "Katso tarjouksesi",
            declinedNotice: "Yrityksesi on kieltäytynyt tästä tarjouspyynnöstä.",
            makeOfferHeading: "Tee tarjous",
            // The provider's price is a binding offer (CLAUDE.md legal model). Said
            // where the number is typed; the customer side of the deal is by phone.
            commitmentNotice: "Hinta on sitova tarjous. Asiakkaan yhteystiedot näkyvät heti lähetyksen jälkeen, ja tilaus sovitaan puhelimitse.",
            sendOfferCta: "Lähetä tarjous",
            confirmOfferQuestion: "Lähetetäänkö sitova tarjous {price} € (alv 0 %)?",
            confirmSendCta: "Kyllä, lähetä tarjous",
            confirmEditCta: "Muokkaa hintaa",
            offerSentNotice: "Tarjous lähetetty asiakkaalle. Asiakkaan puhelinnumero ja sähköposti näkyvät nyt pyynnön tiedoissa.",
            priceRequiredError: "Anna hinta ennen tarjouksen lähettämistä.",
            // The fact only; the action is the amber button under it, which
            // reads "Kirjaudu ja tee tarjous" or "Avaa tarjouspyyntö" by session.
            cannotBidWithLinkNotice: "Tästä linkistä voi lähettää tarjouksen vain, jos kutsu tuli henkilökohtaiseen sähköpostiin.",
            loginToOfferCta: "Kirjaudu ja tee tarjous",
            openInAppCta: "Avaa sovelluksessa",
            loginOpenInAppCta: "Kirjaudu ja avaa sovelluksessa",
            openInAppHint: "Sovelluksessa voit lisätä liitteitä ja viestiä asiakkaan kanssa.",
            declineHeading: "Etkö tarjoa?",
            declineReasonLabel: "Kerro lyhyesti miksi",
            declineReasonHelper: "Näytetään asiakkaalle, jotta hän tietää mitä tapahtui.",
            declineSubmitCta: "Lähetä kieltäytyminen",
            declineCancelCta: "Peruuta",
            declineSentNotice: "Kiitos — asiakkaalle on ilmoitettu, ettet tarjoa.",
            declineReasonRequiredError: "Kirjoita lyhyt perustelu.",
        },
        offerForm: {
            priceTermsLabel: "Hinta-arvion ehdot",
            extraOptionsLabel: "Lisäasetukset",
            validUntilLabel: "Tarjous voimassa",
            attachmentsLabel: "Liitteet",
            pendingNotice: "Tarjous on lähetetty asiakkaalle. Voit yhä päivittää hintaa ja liitteitä kunnes asiakas hyväksyy sen.",
            draftNotice: "Tämä on luonnos. Asiakas ei näe sitä ennen kuin painat \"Lähetä asiakkaalle\".",
            lockedNotice: "Tarjous, hinta ja liitteet näkyvät vain sinulle ja asiakkaalle. Muut pumppuyritykset eivät näe niitä.",
            // Shown directly under the price field in EVERY state. Hinnoittelua
            // pidetään alalla luottamuksellisena, joten takuu on annettava siinä
            // kohdassa jossa epäröinti tapahtuu — ei lomakkeen alalaidassa.
            privacyNotice: "Hintasi näkyy vain asiakkaalle. Muut pumppuyritykset eivät näe sitä.",
            notSentYetNotice: "Asiakas näkee tarjouksen vasta kun lähetät sen.",
            updateDraftCta: "Päivitä luonnos",
            updateOfferCta: "Päivitä tarjous",
            saveDraftCta: "Tallenna luonnos",
            sendCta: "Lähetä asiakkaalle",
        },
        offerAttachments: {
            uploadFailed: "Lataus epäonnistui: {files}",
            selectFilesCta: "Valitse tiedostot",
            disabledHint: "Tallenna luonnos ensin voidaksesi lisätä liitteitä",
            acceptedTypesHint: "JPG / PNG / WEBP / PDF",
            removeAria: "Poista {filename}",
        },
        list: {
            providersOnlyNotice: "Tarjouspyynnöt on tarkoitettu pumppausyrityksille.",
            pageTitle: "Tarjouspyynnöt — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            intro: "Avoimet tarjouspyynnöt ja antamasi tarjoukset.",
            loadFailed: "Tarjouspyyntöjen lataus epäonnistui. Yritä uudelleen.",
            emptyAvoimet: "Ei avoimia tarjouspyyntöjä juuri nyt.",
            emptyTarjotut: "Et ole lähettänyt tarjouksia avoimiin pyyntöihin.",
            emptyVoitetut: "Ei voitettuja tarjouksia vielä.",
            emptyPaattyneet: "Ei päättyneitä tarjouspyyntöjä.",
        },
        tuki: {
            pageTitle: "Ylläpitokeskustelut — Betonijerry",
            pageTitleFallback: "Betonijerry — Pyydä tarjous betonipumppauksesta",
            heading: "Ylläpitokeskustelut",
            intro: "Tukipyyntösi ylläpidon kanssa.",
            tabOpen: "Avoimet",
            tabResolved: "Ratkaistut",
            tabAll: "Kaikki",
            loadFailed: "Tukikeskustelujen lataus epäonnistui. Yritä uudelleen.",
            emptyNotice: "Ei tukikeskusteluja.",
            emptyOpenHint: "Avaa tarvittaessa yhteys ylläpitoon tarjouspyynnön sivulta, tai {link}.",
            emptyOpenHintLink: "lähetä sähköpostia",
            dialogTitleOffer: "Tarjous #{id} — tuki",
            dialogTitleGeneric: "Tukikeskustelu",
            cardTitleOffer: "Tarjous #{id}",
            cardTitleGeneric: "Keskustelu #{id}",
            statusResolved: "Ratkaistu",
            statusOpen: "Avoin",
            openOfferCta: "Avaa tarjous →",
        },
        people: {
            heading: "Henkilöt",
            firstNameLabel: "Etunimi",
            lastNameLabel: "Sukunimi",
            emailLabel: "Sähköposti",
            phoneLabel: "Puhelin",
            memoLabel: "Kommentti",
            roleAdminLabel: "Ylläpitäjä (admin)",
            roleAdminChip: "Admin",
            roleTarjousLabel: "Tarjousoikeus",
            roleTarjousChip: "Tarjous",
            rolePumppariLabel: "Pumppari",
            validateOneFieldRequired: "Täytä vähintään yksi kenttä",
            editDialogTitle: "Muokkaa henkilöä",
            addDialogTitle: "Lisää henkilö",
            globalVisibilityLabel: "Globaali näkyvyys:",
            globalChip: "Globaali",
            companySpecificChip: "Yrityskohtainen",
            makeGlobalCta: "Tee globaaliksi",
            makeGlobalDialogTitle: "Tee globaaliksi?",
            makeGlobalDialogBody: "Henkilöstä tulee näkyvä kaikille järjestelmän yrityksille. Vain ammatilliset yhteystiedot (nimi, sähköposti, puhelin) jaetaan.",
        },
        vehicles: {
            heading: "Ajoneuvot",
            nameLabel: "Nimi",
            regNoLabel: "Rekisterinumero",
            boomSortLabel: "Puomi",
            boomFieldLabel: "Puomi (m)",
            vehicleNoLabel: "Ajoneuvonumero",
            typeLabel: "Ajoneuvotyyppi",
            memoLabel: "Lisätiedot",
            gpsTrackingLabel: "GPS-seuranta asennettu",
            noGpsLabel: "Ei GPS-seurantaa",
            showInactiveLabel: "Näytä myös poistetut käytöstä",
            reactivateFailed: "Palautus käyttöön epäonnistui",
            deactivateAria: "Poista käytöstä",
            deactivateConfirmTitle: "Poistetaanko ajoneuvo käytöstä?",
            deactivateConfirmBody: "Ajoneuvo poistuu aktiivisten listalta mutta säilyy historiatiedoissa. Voit palauttaa sen käyttöön myöhemmin.",
            deactivateFailLabel: "Poista käytöstä epäonnistui",
            reactivateAria: "Palauta käyttöön",
            defaultVehicleName: "Ajoneuvo",
            deletedChip: "Poistettu käytöstä",
            editDialogTitle: "Muokkaa ajoneuvoa",
            addDialogTitle: "Lisää ajoneuvo",
        },
        sijainnit: {
            heading: "Sijainnit",
            nameLabel: "Nimi",
            typeLabel: "Tyyppi",
            streetLabel: "Katuosoite",
            postalCityLabel: "Postinumero ja kaupunki",
            jerryActiveLabel: "Jerry käytössä",
            jerryInactiveLabel: "Jerry ei käytössä",
            jerryActiveHelp: "Sijainti vastaanottaa BetoniJerryn tarjouspyyntöjä alueeltaan.",
            deliveryDistanceLabel: "Toimitusetäisyys (km)",
            deliveryDistanceHelp: "Kuinka kauas tältä sijainnilta ajetaan pumppauskeikoille (linnuntietä).",
            puomiMinLabel: "Puomi min (m)",
            // fb#415: puomiMin is stored but NOT used for matching, so it must not
            // be described as narrowing anything. Do not restore the old
            // "välillä min–max" copy — it told providers the opposite of the truth.
            puomiMinHelp: "Ei rajaa tarjouspyyntöjä — mikä tahansa pumppu voidaan ajaa linjapumppuna ilman puomia. Ilmoita pisin puomi Puomi max -kenttään.",
            puomiMaxLabel: "Puomi max (m)",
            puomiMaxHelp: "Pisin puomi, joka tältä sijainnilta lähtee. Tarjouspyyntö kohdistetaan tänne vain kun pyydetty puomipituus mahtuu tähän. Tyhjä kenttä = ei rajausta.",
            puomiRangeError: "Puomi min ei voi olla suurempi kuin puomi max",
            editDialogTitle: "Muokkaa sijaintia",
            addDialogTitle: "Lisää sijainti",
        },
        liity: {
            areaRequiredError: "Kerro toiminta-alueesi",
            phoneRequiredError: "Puhelinnumero vaaditaan",
            coverageInvalidError: "Anna kantama kilometreinä",
            doneAlert: "Kiitos! Hakemuksesi on vastaanotettu — otamme sinuun pian yhteyttä.",
            doneHint: "Jerry-jäsenyys aktivoidaan käsin. Saat tiedon, kun yrityksesi on liitetty.",
            heading: "Liity BetoniJerryyn",
            introPrefixWithCompany: "{company} ei ole vielä Jerry-palveluntarjoaja. ",
            introPrefixNoCompany: "Yrityksesi ei ole vielä Jerry-palveluntarjoaja. ",
            introBody: "Jerryssä saat betonipumppauksen tarjouspyynnöt suoraan alueeltasi. Täytä lyhyt hakemus — käymme sen läpi ja otamme yhteyttä.",
            alreadyPendingNotice: "Hakemuksesi on jo käsittelyssä. Voit tarvittaessa täydentää tietoja ja lähettää uudelleen.",
            areaFieldLabel: "Toiminta-alue",
            areaFieldHelper: "Esim. Helsinki, Uusimaa",
            coverageFieldLabel: "Kantama (km)",
            coverageFieldHelper: "Kuinka kaukana toimitatte",
            phoneFieldLabel: "Puhelin",
            noteFieldLabel: "Viesti (vapaaehtoinen)",
            submitting: "Lähetetään…",
            submitCta: "Lähetä liittymishakemus",
        },
        recap: {
            heading: "Pyyntö",
            mapAlt: "Karttakuva osoitteesta {address}",
            guidedQuestionsCount: "Asiakas vastasi näihin kysymyksiin ({count})",
            manualSpecNotice: "Asiakas valitsi betonilaadun itse.",
            addressLabel: "Osoite",
            customerLabel: "Asiakas",
            phoneLabel: "Puhelin",
            emailLabel: "Sähköposti",
            kayttokohdeLabel: "Käyttökohde",
            amountLabel: "Määrä",
            floorPlanLabel: "Pohjakuva",
            durationLabel: "Kesto (arvio)",
            boomLabel: "Puomi",
            lineLabel: "Linja",
            concreteDescLabel: "Betonikuvaus",
            driveInstructionsLabel: "Ajo-ohjeet",
            notesLabel: "Lisätiedot",
            customerAttachmentsHeading: "Asiakkaan liitteet",
            respondByPrefix: "Vastaa {date} mennessä · ",
            footerLine: "#{id} · Saapunut {date}",
        },
        listCard: {
            pumpBoomSuffix: "{m} m puomi",
            pumpLineSuffix: "{m} m linja",
            sitePhotoAlt: "Työmaakuva",
            mapAltRevealed: "Karttakuva osoitteesta {address}",
            mapAltMasked: "Karttakuva alueesta {address}",
            noImageAlt: "Ei kuvaa",
            receivedPrefix: "Saapunut {date}",
            viewCta: "Katso →",
            distanceTooltip: "Linnuntietä lähimmältä varikolta",
        },
        entityList: {
            filter: "Suodata",
            add: "Lisää",
            searchPlaceholder: "Hae…",
            clearSearchAria: "Tyhjennä haku",
            sortByLabel: "Järjestä:",
            sortDefault: "Oletus",
            changeSortDirAria: "Vaihda järjestyssuunta",
            loadFailed: "Lataus epäonnistui",
            deleteLabel: "Poista",
            confirmDeleteTitle: "Poistetaanko?",
            confirmDeleteBody: "Tätä toimintoa ei voi peruuttaa.",
            deleteFailed: "Poisto epäonnistui",
            noSearchResults: "Ei hakutuloksia.",
            noData: "Ei vielä tietoja.",
        },
        entityFormDialog: {
            saveFailed: "Tallennus epäonnistui",
            saveLabel: "Tallenna",
            savingLabel: "Tallennetaan…",
        },
        entitySchema: {
            requiredError: "{label} on pakollinen",
            invalidError: "{label} on virheellinen",
            invalidEmail: "Sähköpostiosoite on virheellinen",
        },
        companyBasics: {
            ytunnusPrefix: "Y: {value}",
        },
        hallinta: {
            heading: "Hallinta",
        },
        hallintaApi: {
            vehicleCreateFailed: "vehicle/new ei palauttanut vehicleId:tä",
        },
    },
};
